import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card/card.tsx';
import { SectionLabel } from '../section-label/section-label.tsx';
import { Sparkline, type SparklineTone } from '../sparkline/sparkline.tsx';

export interface StatCardTrend {
  /** The recent history behind the figure, oldest first. */
  values: number[];
  tone?: SparklineTone;
  /** Names the sparkline. Defaults to "Trend". */
  label?: string;
}

export interface StatCardProps {
  /** What is being counted. */
  label: ReactNode;
  /** The figure itself — a node, so it can carry a unit or a second reading. */
  value: ReactNode;
  /** The line under the figure: what it is out of, or over what period. */
  hint?: ReactNode;
  /*
   * How the number is arrived at — the sentence a reader needs before they
   * trust it. Rendered as a quiet line for now; it becomes the body of a
   * tooltip when Tooltip lands, which is a change of rendering, not of API.
   */
  info?: ReactNode;
  trend?: StatCardTrend;
  className?: string;
}

/**
 * One number on a dashboard, with what it means around it.
 *
 * The label is a SectionLabel rather than a card title: a row of four of these
 * is one block of the page, and four green-dotted card titles would read as
 * four sections.
 */
export function StatCard({ label, value, hint, info, trend, className }: StatCardProps) {
  return (
    <Card
      data-slot='stat-card'
      className={cn('flex items-start justify-between gap-4', className)}
    >
      <div className='min-w-0'>
        <SectionLabel data-slot='stat-card-label'>{label}</SectionLabel>
        <p
          data-slot='stat-card-value'
          className='mt-2 text-xl font-semibold text-ink tabular-nums'
        >
          {value}
        </p>
        {hint !== undefined && hint !== null && (
          <p data-slot='stat-card-hint' className='mt-1 text-xs text-ink-muted'>
            {hint}
          </p>
        )}
        {info !== undefined && info !== null && (
          <p data-slot='stat-card-info' className='mt-2 text-xs text-ink-muted'>
            {info}
          </p>
        )}
      </div>
      {trend !== undefined && (
        <Sparkline
          values={trend.values}
          tone={trend.tone}
          showLastPoint
          ariaLabel={trend.label ?? 'Trend'}
          className='mt-1 shrink-0'
        />
      )}
    </Card>
  );
}
